import { useEffect, useState, type FormEvent } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import { accountNumberDigitsFromInput, routingNumberDigitsFromInput } from '../../../../shared/formatting';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';
import { PAY_DAY_KIND } from '../../types';
import type { StepProps } from '../AdminSignUpPage';

// Mirrors EPN.PayrollConfig.Api's PayFrequency enum exactly (Weekly=1, BiWeekly=2, SemiMonthly=3,
// Monthly=4, Quarterly=5). Quarterly resolves to the KotaPay funding partner server-side, same as
// Weekly/BiWeekly/SemiMonthly (see FundingPartnerResolver) — this form never sends a funding partner.
const FREQUENCIES: { value: number; label: string }[] = [
  { value: 4, label: 'Monthly' },
  { value: 3, label: 'Twice a Month' },
  { value: 2, label: 'Bi-weekly' },
  { value: 1, label: 'Weekly' },
  { value: 5, label: 'Quarterly' },
];

// Mirrors EPN.PayrollConfig.Api's own PayWeekday domain enum exactly (0=Sunday..6=Saturday, matching
// System.DayOfWeek's numbering) — the approved FINAL HTML only offers Monday-Friday, so only those 5
// values are ever presented, but their numeric meaning is now an explicit, backend-tested contract
// (PayScheduleValidationTests) rather than a UI-only assumption.
const WEEKDAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

// A UI-local sentinel for the day-of-month <select> — never sent over the wire. On submit this becomes
// `{ day: null, kind: PAY_DAY_KIND.LastDayOfMonth }`, matching EPN.PayrollConfig.Api's own PayDayKind
// pairing (no magic day-of-month integer is ever transmitted for "Last Day of Month").
const LAST_DAY = 'LAST_DAY_OF_MONTH';
type DayOfMonthSelection = number | typeof LAST_DAY | '';

function toDayAndKind(selection: DayOfMonthSelection): { day: number | null; kind: number | null } {
  if (selection === '') return { day: null, kind: null };
  if (selection === LAST_DAY) return { day: null, kind: PAY_DAY_KIND.LastDayOfMonth };
  return { day: selection, kind: PAY_DAY_KIND.DayOfMonth };
}

function fromDayAndKind(day: number | null, kind: number | null): DayOfMonthSelection {
  return kind === PAY_DAY_KIND.LastDayOfMonth ? LAST_DAY : (day ?? '');
}

/** Step 3 — Banking Details and Payroll Setup. Runtime order is fixed and load-bearing (Admin Sign Up
 * Decision 7): the pay schedule must be saved in PayrollConfig.Api BEFORE the funding account is created in
 * PaymentProcessing.Api, because that module derives and persists the NatPay/KotaPay assignment by reading
 * the just-saved schedule server-side — this step never sends a funding-partner value itself.
 * <p>
 * Pay Period Start Date is required (the approved FINAL HTML's own field) and, along with the rest of the
 * schedule, is hydrated from `getActivePaySchedule()` on mount so returning to this step (Go Back from
 * Step 4) does not silently discard a schedule already saved this session. */
export interface BankingStepProps extends StepProps {
  /** EPN-35 Partner Sign Up override — default matches Admin Sign Up's own approved copy exactly. */
  heading?: string;
}

export function BankingStep({ goNext, goBack, heading = 'Step 3: Banking Details and Payroll Setup' }: BankingStepProps) {
  const [bankName, setBankName] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountType, setAccountType] = useState<'' | 'Checking' | 'Savings'>('');
  const [accountNumber, setAccountNumber] = useState('');
  const [frequency, setFrequency] = useState(4);
  const [firstPayDay, setFirstPayDay] = useState<DayOfMonthSelection>('');
  const [secondPayDay, setSecondPayDay] = useState<DayOfMonthSelection>('');
  const [payPeriodStartDate, setPayPeriodStartDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSemiMonthly = frequency === 3;
  const isWeekday = frequency === 1 || frequency === 2;
  const dayOfMonthOptions: { value: DayOfMonthSelection; label: string }[] = [
    ...DAYS_OF_MONTH.map((d) => ({ value: d, label: String(d) })),
    { value: LAST_DAY, label: 'Last Day of Month' },
  ];
  const firstPayDayOptions = isWeekday ? WEEKDAYS.map((w) => ({ value: w.value as DayOfMonthSelection, label: w.label })) : dayOfMonthOptions;

  useEffect(() => {
    void adminSignupGateway.getActivePaySchedule().then((existing) => {
      if (!existing) return;
      setFrequency(existing.frequency);
      setFirstPayDay(fromDayAndKind(existing.firstPayDay, existing.firstPayDayKind));
      setSecondPayDay(fromDayAndKind(existing.secondPayDay, existing.secondPayDayKind));
      setPayPeriodStartDate(existing.payPeriodStartDate ?? '');
    });
    void adminSignupGateway.getFundingAccount().then((existing) => {
      if (!existing) return;
      setBankName(existing.bankName);
      setAccountType(existing.accountType as 'Checking' | 'Savings');
      // Routing/account numbers are not returned by the read endpoint (masked/omitted for security), so
      // they are intentionally left for the user to re-enter — never pre-filled from a masked value.
    });
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (firstPayDay === '') {
      setError('Select a Pay Day.');
      return;
    }
    if (isSemiMonthly && secondPayDay === '') {
      setError('Select a second Pay Day that is approximately 15 days after the first Pay Day.');
      return;
    }
    if (!payPeriodStartDate) {
      setError('Select a Pay Period Start Date.');
      return;
    }

    setSubmitting(true);
    try {
      const first = toDayAndKind(firstPayDay);
      const second = isSemiMonthly ? toDayAndKind(secondPayDay) : { day: null, kind: null };

      // 1. Pay schedule first — PaymentProcessing.Api reads this back to derive the funding partner.
      // CreateForOnboardingAsync upserts by company, so resubmitting after a funding-account failure below
      // updates this same schedule in place rather than creating a duplicate.
      try {
        await adminSignupGateway.savePaySchedule({
          name: 'Default Schedule',
          frequency,
          firstPayDay: first.day,
          firstPayDayKind: first.kind,
          secondPayDay: second.day,
          secondPayDayKind: second.kind,
          payPeriodStartDate,
        });
      } catch (err) {
        setError(friendlyErrorMessage(err, 'The payroll schedule could not be saved'));
        return;
      }

      // 2. Banking — the resulting funding partner is server-derived and returned, never sent by this form.
      try {
        await adminSignupGateway.saveFundingAccount({ bankName, routingNumber, accountNumber, accountType: accountType as 'Checking' | 'Savings' });
      } catch (err) {
        // The pay schedule saved successfully above and is left in place (no rollback, no duplicate) — the
        // user can correct their banking details and click "Save and Continue" again without re-entering
        // the payroll schedule.
        setError(friendlyErrorMessage(err, 'The funding account could not be saved'));
        return;
      }

      goNext();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="screen active" data-testid="banking-step">
      <div className="screen-icon" aria-hidden="true">
        🏦
      </div>
      <h2>{heading}</h2>

      <form onSubmit={handleSubmit}>
        <div className="section-heading">Banking Information</div>
        <div className="field-grid">
          <div className="field">
            <label htmlFor="bankName">
              Bank Name <span className="req">*</span>
            </label>
            <input className="control" id="bankName" value={bankName} onChange={(e) => setBankName(e.target.value)} required />
          </div>

          <div className="field">
            <label htmlFor="routingNumber">
              Bank Routing Number <span className="req">*</span>
            </label>
            <input
              className="control"
              id="routingNumber"
              inputMode="numeric"
              autoComplete="off"
              value={routingNumber}
              onChange={(e) => setRoutingNumber(routingNumberDigitsFromInput(e.target.value))}
              required
              placeholder="9 digits"
            />
          </div>

          <div className="field">
            <label htmlFor="accountType">
              Account Type <span className="req">*</span>
            </label>
            <select className="control" id="accountType" value={accountType} onChange={(e) => setAccountType(e.target.value as '' | 'Checking' | 'Savings')} required>
              <option value="">Select</option>
              <option value="Checking">Checking</option>
              <option value="Savings">Savings</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="accountNumber">
              Bank Account Number <span className="req">*</span>
            </label>
            <input
              className="control"
              id="accountNumber"
              inputMode="numeric"
              autoComplete="off"
              value={accountNumber}
              onChange={(e) => setAccountNumber(accountNumberDigitsFromInput(e.target.value))}
              required
              placeholder="4-17 digits"
            />
          </div>
        </div>

        <div className="section-heading">Payroll Information</div>
        <div className="field-grid">
          <div className="field">
            <label htmlFor="frequency">
              Pay Schedule <span className="req">*</span>
            </label>
            <select
              className="control"
              id="frequency"
              value={frequency}
              onChange={(e) => {
                setFrequency(Number(e.target.value));
                setFirstPayDay('');
                setSecondPayDay('');
              }}
            >
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="payPeriodStartDate">
              Pay Period Start Date <span className="req">*</span>
            </label>
            <input
              className="control"
              id="payPeriodStartDate"
              type="date"
              value={payPeriodStartDate}
              onChange={(e) => setPayPeriodStartDate(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="firstPayDay">
              {isSemiMonthly ? 'First Pay Day' : 'Pay Day'} <span className="req">*</span>
            </label>
            <select
              className="control"
              id="firstPayDay"
              value={firstPayDay}
              onChange={(e) => setFirstPayDay(e.target.value === '' ? '' : e.target.value === LAST_DAY ? LAST_DAY : Number(e.target.value))}
              required
            >
              <option value="">Select</option>
              {firstPayDayOptions.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {isSemiMonthly && (
            <div className="field">
              <label htmlFor="secondPayDay">
                Second Pay Day <span className="req">*</span>
              </label>
              <select
                className="control"
                id="secondPayDay"
                value={secondPayDay}
                onChange={(e) => setSecondPayDay(e.target.value === '' ? '' : e.target.value === LAST_DAY ? LAST_DAY : Number(e.target.value))}
                required
              >
                <option value="">Select</option>
                {dayOfMonthOptions.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && (
          <p className="error-line" role="alert">
            <span className="info-icon">i</span>
            <span>{error}</span>
          </p>
        )}

        <div className="actions">
          <button className="btn btn-back" type="button" onClick={goBack}>
            Go Back
          </button>
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
